import type { DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import Datetime from 'utils/datetime/datetime'
import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import type { AppliedSettingSeries, AppliedSettingsTableItem } from './appliedSettingValues.types'
import { isUnitNameTranslated } from './seriesCatalogue.helper'

type CellValue = string | null

/** Mirrors the table's own name search, which ignores terms shorter than two characters. */
const MIN_SEARCH_TERM_LENGTH = 2

type ColumnGroup = { key: string; label: string }

/**
 * The unit an item's columns are grouped under; an item without a sub title is its own column. Keyed by
 * unit name, because two units can share a display title.
 */
function toColumnGroup(
  item: AppliedSettingsTableItem,
  t: (translation: string) => string,
  translations?: { [key: string]: string }
): ColumnGroup | null {
  if (!item.sub_title) {
    return null
  }
  return { key: item.unit_name, label: t(getPrettyName(item.title, translations)) }
}

function toPropertyHeader(
  item: AppliedSettingsTableItem,
  t: (translation: string) => string,
  translations?: { [key: string]: string }
): string {
  return item.sub_title ? t(item.sub_title) : t(getPrettyName(item.title, translations))
}

/** The graph's tooltip shows two decimals, so the table does too — without padding whole numbers with zeros. */
const MAX_DECIMALS = 2

function toCellValue(value: number | null | undefined): CellValue {
  if (value === null || value === undefined) {
    return null
  }
  return String(Number(value.toFixed(MAX_DECIMALS)))
}

/** The unit and property as one string, which is what a search term is matched against. */
function toColumnHeader(
  item: AppliedSettingsTableItem,
  t: (translation: string) => string,
  translations?: { [key: string]: string }
): string {
  const group = toColumnGroup(item, t, translations)
  const property = toPropertyHeader(item, t, translations)

  return group === null ? property : `${group.label}\n${property}`
}

function getColumnItems(items: AppliedSettingsTableItem[]): AppliedSettingsTableItem[] {
  return items.filter((item) => item?.data_id)
}

/** The series whose table column header contains the search term, so the graph shows what the table does. */
export function filterSeriesBySearchTerm(
  series: AppliedSettingSeries[],
  searchTerm: string,
  t: (translation: string) => string,
  translations: DigitalTwinTranslations
): AppliedSettingSeries[] {
  if (searchTerm.length < MIN_SEARCH_TERM_LENGTH) {
    return series
  }

  const lowerCaseTerm = searchTerm.toLowerCase()
  const items = toTableItems(series, translations)
  return series.filter((_, index) =>
    toColumnHeader(items[index], t, translations).toLowerCase().includes(lowerCaseTerm)
  )
}

export function getTableSeries(
  series: AppliedSettingSeries[],
  legendReturnIds: ReadonlySet<string>,
  hiddenReturnIds: ReadonlySet<string>
): AppliedSettingSeries[] {
  return series.filter(({ returnId }) => !(legendReturnIds.has(returnId) && hiddenReturnIds.has(returnId)))
}

export function mapDatasetsToTableData(
  datasets: Dataset[],
  items: AppliedSettingsTableItem[],
  t: (translation: string) => string,
  translations?: { [key: string]: string }
): CellValue[][] {
  if (!datasets.length || !items.length) {
    return []
  }

  const columnItems = getColumnItems(items)
  if (!columnItems.length) {
    return []
  }

  const times = new Set<ISODateTime>()
  const valueByReturnIdAndTime: Record<string, Record<string, CellValue>> = {}

  datasets.forEach((dataset) => {
    valueByReturnIdAndTime[dataset.return_id] = {}

    dataset.times.forEach((datasetTime, index) => {
      const normalizedTime = Datetime.toISOString(datasetTime as ISODateTime)
      times.add(normalizedTime)

      valueByReturnIdAndTime[dataset.return_id][normalizedTime] = toCellValue(dataset.values[index])
    })
  })

  const sortedTimes = Array.from(times).sort(Datetime.compare)

  const headers = [
    `${t('Time')}\n(${t('Local time')})`,
    ...columnItems.map((item) => toPropertyHeader(item, t, translations)),
  ]

  const rows: CellValue[][] = sortedTimes.map((time) => {
    const row: CellValue[] = [Datetime.toLocalTime(time, 'longDay')]

    columnItems.forEach((item) => {
      row.push(valueByReturnIdAndTime[item.data_id]?.[time] ?? null)
    })

    return row
  })

  return [headers, ...rows]
}

/** The unit heading each column of `mapDatasetsToTableData`, in the same order; null where a column has none. */
export function toColumnGroups(
  items: AppliedSettingsTableItem[],
  t: (translation: string) => string,
  translations?: { [key: string]: string }
): (ColumnGroup | null)[] {
  return [null, ...getColumnItems(items).map((item) => toColumnGroup(item, t, translations))]
}

export function toTableItems(
  series: AppliedSettingSeries[],
  translations: DigitalTwinTranslations
): AppliedSettingsTableItem[] {
  return series.map((entry) => ({
    title: isUnitNameTranslated(entry.unitName, translations) ? entry.unitName : entry.unitTitle,
    sub_title: entry.propertyTitle,
    data_id: entry.returnId,
    unit_name: entry.unitName,
  }))
}
